"use client";

import React, { useState } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";

/**
 * Image gallery for pricing page using next/image (no thumbnails).
 * First image uses priority for LCP. Desktop/mobile image sets with same breakpoint as before.
 */
const PricingImageGallery = ({ imagesDesktop = [], imagesMobile = [], altText = "" }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const desktopImages = Array.isArray(imagesDesktop) ? imagesDesktop : [];
  const mobileImages = Array.isArray(imagesMobile) ? imagesMobile : [];
  const hasDesktop = desktopImages.length > 0;
  const hasMobile = mobileImages.length > 0;
  const desktopCount = desktopImages.length;
  const mobileCount = mobileImages.length;
  const totalSlides = Math.max(desktopCount, mobileCount, 1);

  const goPrev = () => {
    setCurrentIndex((i) => (i <= 0 ? totalSlides - 1 : i - 1));
  };
  const goNext = () => {
    setCurrentIndex((i) => (i >= totalSlides - 1 ? 0 : i + 1));
  };

  if (!hasDesktop && !hasMobile) return null;

  const currentDesktopSrc = desktopImages[currentIndex % desktopCount]?.src;
  const currentMobileSrc = mobileImages[currentIndex % mobileCount]?.src;

  return (
    <>
      {/* Desktop: viewport > 480px — 16:9 so image fills with less space above/below */}
      <div className="relative hidden w-full min-[481px]:block overflow-hidden aspect-video">
        {currentDesktopSrc && (
          <>
            <Image
              src={currentDesktopSrc}
              alt={altText ? `${altText} - Image ${currentIndex + 1}` : `Gallery image ${currentIndex + 1}`}
              fill
              className="object-contain object-top object-left"
              sizes="(max-width: 768px) 100vw, 60vw"
              priority={currentIndex === 0}
            />
            {desktopCount > 1 && (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors"
                  aria-label="Previous image"
                >
                  <ChevronLeft fontSize="medium" />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors"
                  aria-label="Next image"
                >
                  <ChevronRight fontSize="medium" />
                </button>
              </>
            )}
          </>
        )}
      </div>

      {/* Mobile: viewport <= 480px — image takes full column width, height from intrinsic ratio */}
      <div className="relative block w-full min-[481px]:hidden">
        {currentMobileSrc && (
          <>
            <Image
              src={currentMobileSrc}
              alt={altText ? `${altText} - Image ${currentIndex + 1}` : `Gallery image ${currentIndex + 1}`}
              width={800}
              height={600}
              className="w-full h-auto object-contain"
              sizes="100vw"
              priority={currentIndex === 0}
            />
            {mobileCount > 1 && (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors"
                  aria-label="Previous image"
                >
                  <ChevronLeft fontSize="medium" />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors"
                  aria-label="Next image"
                >
                  <ChevronRight fontSize="medium" />
                </button>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default PricingImageGallery;
